import { useContext, useEffect, useState, useCallback } from 'react';
import { useQueryParams } from 'use-query-params';
import {
  RecentReports,
  RecentReportSerialized,
  useSession,
  AppConstants,
  publish,
} from '@ltvco/refresh-lib';
import {
  Button,
  Pagination,
  Stack,
  Text,
  Icons,
  Avatar,
  useMediaQuery,
  useTheme,
} from '@ltvco/refresh-lib/theme';

import BBQ, {
  type ReportType,
  generateQueryConfig,
  stringifyParams,
  useRecentReports,
} from '../../components/BBQ';

const queryConfig = generateQueryConfig();

export function RecentReportsPage() {
  const {
    images: { searchLogo },
    config: { reportTypeFilterOptions },
  } = useContext(AppConstants);
  const theme = useTheme();
  const { session } = useSession();

  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'));
  const collapseFilterOptions = isSmall || (!isMedium && isLarge);

  type ReportFilter = ReportType | 'monitored_report';

  const [editReportsMode] = useState(false);
  const [filterTypes, setFilterTypes] = useState<ReportFilter[]>([]);
  const [queryParams, setQueryParams] = useQueryParams(queryConfig);
  const filteredParams = stringifyParams(queryParams);
  const { data: queryData, isLoading } = useRecentReports(filteredParams);
  const reportTypes: ReportType[] = filterTypes.filter(
    (type) => type !== 'monitored_report'
  );

  const totalReports =
    queryData?.meta?.report_quantities?.total_reports_count || 0;
  const count = Math.ceil(totalReports / 20);
  const reports = (queryData?.reports as RecentReportSerialized[]) || [];

  const { sort_by, sort_direction } = queryParams;

  const updateUrlFilter = useCallback(
    () => setQueryParams({ report_type: reportTypes }),
    [setQueryParams, reportTypes]
  );
  useEffect(() => updateUrlFilter(), [updateUrlFilter]);

  function handlePageChange(page: number) {
    setQueryParams({ page });
    window.scrollTo(0, 0);
  }

  function hasRunReports() {
    const retentionInfo = session?.account?.account?.retention_info;
    if (retentionInfo) {
      return (
        retentionInfo.ran_email_search ||
        retentionInfo.ran_people_search ||
        retentionInfo.ran_property_search ||
        retentionInfo.ran_reverse_phone_search ||
        retentionInfo.ran_username_search
      );
    }

    return false;
  }

  useEffect(() => {
    document.title = 'My Contacts - PeopleSmart';
  }, []);

  if (!isLoading && reports.length === 0 && !hasRunReports()) {
    return (
      <Stack textAlign={'center'}>
        <Avatar
          src={searchLogo}
          sx={{ width: 80, height: 80, margin: 'auto' }}
          variant="square"
        />
        <Text
          variant={'h1'}
          fontSize={40}
          fontWeight={500}
          display={'inline-block'}
          marginTop={1.5}
          marginBottom={0}
        >
          Start searching today
        </Text>
        <Text
          variant={'h3'}
          fontSize={28}
          fontWeight={300}
          display={'inline-block'}
        >
          Select a search type at the top of your screen
        </Text>
        <Button
          onClick={() => publish('openPersonSearch')}
          variant="contained"
          color="primary"
          size="large"
          sx={{ margin: 'auto' }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Icons.Search fontSize="small" />
            <Text component="span">Start searching</Text>
          </Stack>
        </Button>
      </Stack>
    );
  }

  return (
    <>
      <RecentReports
        reportList={reports}
        isLoading={isLoading}
        editReportsMode={editReportsMode}
        FilterComponent={
          <Stack spacing={2} sx={{ marginY: 2 }}>
            <BBQ.Search />
            <Stack
              direction={collapseFilterOptions ? 'column' : 'row'}
              spacing={collapseFilterOptions ? 2 : 0}
              justifyContent="space-between"
            >
              <BBQ.Filters
                reportTypeOptions={reportTypeFilterOptions}
                reportTypes={reportTypes}
                setReportTypes={setFilterTypes}
              />
              <BBQ.Sort
                currentSort={{ sort_by, sort_direction }}
                setQueryParams={setQueryParams}
              />
            </Stack>
          </Stack>
        }
      />
      {!isLoading && count > 1 && (
        <Stack spacing={2} direction={'row'} sx={{ justifyContent: 'center' }}>
          <Pagination
            page={queryParams.page}
            count={count}
            onChange={(_: any, page: number) => handlePageChange(page)}
            variant="outlined"
            shape="rounded"
            sx={{
              '& .MuiPaginationItem-page, & .MuiPaginationItem-ellipsis': {
                display: 'none',
              },
            }}
          />
        </Stack>
      )}
    </>
  );
}
